import { useCallback } from 'react';

import type { FormInstance } from '@arco-design/web-react';

import type { VendorDeviceBillOption, VendorDeviceSelection } from '@/delivery-type-plugin/components';
import {
  VendorAndDeviceRadioList,
  VendorDeviceSelectionRequiredField,
  hasVendorDeviceSelection,
  resolveVendorDeviceRequiredValidateField,
} from '@/delivery-type-plugin/components';
import { findVendorDeviceOption, optionToSelection } from '@/delivery-type-plugin/components/vendorAndDeviceRadioList';

import { EBM_NODE_RESOURCE_CODE } from '../constants';
import { EBM_RESOURCE_CONFIG_OPTION_META_KEYS } from '../fieldKeys';
import type { EbmResourceConfigBlockOptions, EbmResourceConfigPaths } from '../types';
import { applyBillingResourceSelection, clearBillingResourceSelection } from '../utils/billingResourceMeta';

interface BillingResourceSelectorProps {
  billModelId: string;
  /** 交付类型 id，计价 resolve 必传 */
  deploymentTypeId?: string;
  form: FormInstance;
  paths: EbmResourceConfigPaths;
  disabled: boolean;
  skipFetch?: boolean;
  value: VendorDeviceSelection | null;
  onSelectionChange: (value: VendorDeviceSelection | null) => void;
  options?: EbmResourceConfigBlockOptions;
}

/** 计价模式厂商设备校验字段前缀 */
const EBM_BILLING_VENDOR_DEVICE_PREFIX = 'ebmResourceConfig';

/** 计价模式：ElasticBareMetalNode 厂商设备单选（不拉 resource-configurations，提交计费 composition） */
export function BillingResourceSelector({
  billModelId,
  deploymentTypeId,
  form,
  paths,
  disabled,
  skipFetch,
  value,
  onSelectionChange,
  options,
}: BillingResourceSelectorProps) {
  const resourceCode = options?.resourceCode || EBM_NODE_RESOURCE_CODE;

  const readBillingSelection = useCallback((): VendorDeviceSelection | null | undefined => {
    const optionMeta = form.getFieldValue('optionMeta') as Record<string, unknown> | undefined;
    return optionMeta?.[EBM_RESOURCE_CONFIG_OPTION_META_KEYS.ebmNode] as VendorDeviceSelection | undefined;
  }, [form]);

  const requiredField = resolveVendorDeviceRequiredValidateField(EBM_BILLING_VENDOR_DEVICE_PREFIX);

  /**
   * 列表加载后：无选中保持空选（不默认 options[0]）；有选中但无法匹配则清空残留态。
   * 空列表（加载中 / 缺 deploymentTypeId）不清理，避免刚选中就被冲掉导致预览要选两次。
   * skipFetch（固定规格）：信任 hydrate 的 resourceConfiguration，仅校验选中仍在列表中。
   */
  const handleOptionsChange = useCallback(
    (nextOptions: VendorDeviceBillOption[]) => {
      if (disabled) return;
      if (!nextOptions.length) return;

      const current = readBillingSelection();
      if (!hasVendorDeviceSelection(current)) {
        return;
      }

      const found = findVendorDeviceOption(nextOptions, current);
      if (found) {
        if (skipFetch) return;
        const selection = optionToSelection(found);
        onSelectionChange(selection);
        applyBillingResourceSelection(form, paths, selection);
        return;
      }

      /** billItemId 命中即视为仍有效（避免 useSubResourcePricing 未回填时误清） */
      const billItemId = current?.billItemId?.trim();
      const byBillItem = billItemId ? nextOptions.find((item) => item.billItemId === billItemId) : undefined;
      if (byBillItem) {
        if (skipFetch) return;
        const selection = optionToSelection(byBillItem);
        onSelectionChange(selection);
        applyBillingResourceSelection(form, paths, selection);
        return;
      }

      onSelectionChange(null);
      clearBillingResourceSelection(form, paths);
      void form.validate([requiredField]).catch(() => undefined);
    },
    [disabled, form, onSelectionChange, paths, readBillingSelection, requiredField, skipFetch],
  );

  return (
    <VendorDeviceSelectionRequiredField fieldPrefix={EBM_BILLING_VENDOR_DEVICE_PREFIX} readSelection={readBillingSelection}>
      <VendorAndDeviceRadioList
        billModelId={billModelId}
        deploymentTypeId={deploymentTypeId}
        resourceCode={resourceCode}
        value={value}
        disabled={disabled}
        skipFetch={skipFetch}
        hideTitle
        onOptionsChange={handleOptionsChange}
        onChange={(next) => {
          onSelectionChange(next);

          if (!next) {
            clearBillingResourceSelection(form, paths);
            void form.validate([requiredField]).catch(() => undefined);
            return;
          }

          applyBillingResourceSelection(form, paths, next);
          void form.validate([requiredField]).catch(() => undefined);
        }}
      />
    </VendorDeviceSelectionRequiredField>
  );
}
